import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider.js";
import { useEmailVerification } from "../auth/EmailVerification.js";

/**
 * Post-login email verification — the skippable step right after the intake
 * funnel creates an account (and the target of the persistent "verify your
 * email" banner). The account already works; this just verifies the email so
 * password recovery is available. A code is emailed on entry; the user redeems
 * it or taps "I'll do this later".
 */
export function VerifyEmail() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { sendEmailVerification, confirmEmailVerification } = useAuth();
  const { loading, verified, email, refresh } = useEmailVerification();

  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Where to go once done. The funnel passes the just-created RFQ so we land on
  // its "submitted" confirmation; otherwise fall back to the app home.
  const rfqId = params.get("rfqId");
  const doneTarget = rfqId ? `/app/rfqs/${rfqId}/submitted` : "/app";

  // Send a code once on entry (unless already verified). The ref guard keeps
  // StrictMode's double-invoke — and any re-render — from emailing twice.
  const sentRef = useRef(false);
  useEffect(() => {
    if (loading) return;
    if (verified) {
      navigate(doneTarget, { replace: true });
      return;
    }
    if (sentRef.current) return;
    sentRef.current = true;
    sendEmailVerification()
      .then(() => setInfo("We emailed you a 6-digit code."))
      .catch((err) =>
        setError(err instanceof Error ? err.message : "Couldn't send a verification code."),
      );
  }, [loading, verified, doneTarget, navigate, sendEmailVerification]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      await confirmEmailVerification({ code });
      await refresh();
      navigate(doneTarget, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "That code didn't work. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function onResend() {
    setError(null);
    setInfo(null);
    try {
      await sendEmailVerification();
      setInfo("Sent a new code. Check your inbox.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not resend the code.");
    }
  }

  return (
    <main className="mx-auto max-w-md px-6 py-10">
      <h1 className="text-2xl font-bold text-slate-900">Verify your email</h1>
      <p className="mt-2 text-sm text-slate-600">
        Enter the 6-digit code we emailed to {email ?? "your inbox"}. This lets you reset your
        password later if you ever need to.
      </p>

      <form className="mt-8 space-y-5" onSubmit={onSubmit} noValidate>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">Verification code</span>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            maxLength={6}
            required
            autoFocus
            className="block w-full min-h-[44px] rounded-xl border border-slate-300 bg-white px-4 text-base tracking-[0.4em] text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-600"
          />
        </label>

        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}
        {info && <p className="text-sm text-teal-700">{info}</p>}

        <button
          type="submit"
          disabled={submitting || code.length < 6}
          className="inline-flex min-h-[44px] w-full items-center justify-center rounded-xl bg-teal-700 px-6 text-base font-semibold text-white shadow-sm active:bg-teal-800 disabled:opacity-60"
        >
          {submitting ? "Verifying…" : "Verify email"}
        </button>
        <button
          type="button"
          onClick={onResend}
          className="block w-full min-h-[44px] text-sm font-medium text-teal-700"
        >
          Resend code
        </button>
      </form>

      <button
        type="button"
        onClick={() => navigate(doneTarget, { replace: true })}
        className="mt-2 block w-full min-h-[44px] text-sm font-medium text-slate-500"
      >
        I&rsquo;ll do this later
      </button>
    </main>
  );
}
